import { type Country, type CountryVariantTemplate, useCountries, useUserCountryQueries } from '@capsicum/common';
import { type ComponentRef, forwardRef, useCallback, useMemo } from 'react';

import { Resource } from '../../../atoms/resource/Resource';
import {
	BaseCountrySelectInputSkeleton,
	BaseCountrySelectTemplate,
	CountryCodeWrapper,
	GetCountryFlag
} from '../country-select';
import { BaseSelectInput, type BaseSelectInputProps } from '../select-input/BaseSelectInput';

export type BaseCountrySelectInputProps = Omit<BaseSelectInputProps<Country>, 'data' | 'itemTemplate' | 'onSearch'> & {
	variant?: CountryVariantTemplate;
};

export const BaseCountrySelectInput = forwardRef<ComponentRef<typeof BaseSelectInput>, BaseCountrySelectInputProps>(
	({ labelProp, value, variant = 'country', ...props }, ref) => {
		const { countriesQuery } = useCountries();

		const userCountry = useUserCountryQueries();

		const initialValue = useMemo(
			() => value || userCountry || countriesQuery?.data?.[0],
			[countriesQuery.data, userCountry, value]
		);

		const onSearch = useCallback((data: Array<Country>, text: string) => {
			return (data ?? []).filter(
				({ internationalCallingCode, iso2, name }) =>
					iso2?.toLowerCase().includes(text.toLowerCase()) ||
					internationalCallingCode?.toLowerCase().includes(text.toLowerCase()) ||
					name?.toLowerCase().includes(text.toLowerCase())
			);
		}, []);

		const countryListLabelProp = useCallback(
			(item: Country) => (
				<CountryCodeWrapper>
					{['countryWithCodeAndFlag', 'countryWithFlag'].includes(variant) && (
						<GetCountryFlag iconSize={16} item={item} />
					)}

					{variant === 'countryWithCodeAndFlag' ? item.internationalCallingCode : item.name}
				</CountryCodeWrapper>
			),
			[variant]
		);

		const itemTemplate = useCallback(
			({ item }: { item: Country }) => {
				return <BaseCountrySelectTemplate item={item} variant={variant} />;
			},
			[variant]
		);

		return (
			<Resource r={countriesQuery} skeleton={<BaseCountrySelectInputSkeleton />}>
				{data => (
					<BaseSelectInput
						customLabelProp={
							['countryWithCodeAndFlag', 'countryWithFlag'].includes(variant)
								? countryListLabelProp
								: undefined
						}
						data={data}
						detached={false}
						itemTemplate={itemTemplate}
						itemTextEllipsis
						labelProp={labelProp}
						ref={ref}
						value={initialValue}
						withExtraBottomSheetPadding={false}
						withSearch
						onSearch={onSearch}
						{...props}
					/>
				)}
			</Resource>
		);
	}
);

BaseCountrySelectInput.displayName = 'BaseCountrySelectInput';
